// TradeLicense.jsx
import React from "react";

export default function TradeLicense() {
  return (
    <div className="min-h-screen bg-white">
      {/* HERO SECTION */}
      <section className="max-w-6xl mx-auto px-4 py-12 grid md:grid-cols-2 gap-10 items-center">
        {/* Left: Text */}
        <div>
          <p className="text-sm tracking-[0.25em] text-gray-500 uppercase mb-3">
            Trade License
          </p>
          <h1 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
            Trade License Registration for Businesses
          </h1>
          <div className="w-20 h-1 bg-yellow-500 mb-6" />
          <p className="text-gray-700 mb-4">
            Hawkins &amp; Brooks provides professional assistance for Trade
            License registration, ensuring businesses operate in compliance with
            local municipal laws and regulations across various jurisdictions.
          </p>
          <p className="text-gray-700">
            From navigating municipal-specific requirements to preparing
            application forms and coordinating with local authorities, the
            process ensures your business receives legal authorization to trade
            and operate without compliance disruptions.
          </p>
        </div>

        {/* Right: Image (TRADE LICENSE - Guaranteed working local business storefront) */}
        <div className="relative">
          <div className="absolute -inset-4 border border-yellow-400 rounded-3xl opacity-40 hidden md:block" />
          <div className="relative rounded-3xl overflow-hidden shadow-xl border border-gray-100">
            <img
              src="https://images.pexels.com/photos/2284166/pexels-photo-2284166.jpeg?auto=compress&cs=tinysrgb&w=1200"
              alt="Local business shop obtaining municipal Trade License"
              className="w-full aspect-square object-cover"
            />
          </div>
        </div>
      </section>

      {/* [Rest of the code remains exactly the same - only image changed] */}
      {/* PROFESSIONAL SUPPORT & DOCUMENTS SECTION */}
      <section className="border-t border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto px-4 py-10 grid md:grid-cols-2 gap-12">
          {/* Professional compliance support */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Professional Compliance Support by Hawkins &amp; Brooks
            </h2>
            <p className="text-gray-700 mb-4">
              Hawkins &amp; Brooks offers end-to-end assistance for Trade License
              applications across municipal corporations, ensuring compliance
              with local regulations and streamlined approval processes.
            </p>
            <div className="bg-gray-50 border border-yellow-100 rounded-xl p-5">
              <ul className="space-y-2 text-gray-800 list-disc list-inside">
                <li>
                  Municipal-specific guidance on eligibility, application forms,
                  and local compliance requirements for trade operations.
                </li>
                <li>
                  Document preparation and verification to meet municipal
                  standards for identity, premises, and business authorization.
                </li>
                <li>
                  Application filing and follow-up with municipal authorities to
                  secure timely license issuance and avoid delays.
                </li>
                <li>
                  Renewal tracking and compliance advisory to maintain valid
                  trade authorization throughout business operations.
                </li>
              </ul>
            </div>
          </div>

          {/* Documents required */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Documents Required for Trade License
            </h2>
            <p className="text-gray-700 mb-4">
              Hawkins &amp; Brooks assists in compiling the standard set of
              documents required by municipal authorities for Trade License
              applications.
            </p>
            <div className="bg-gray-50 border border-yellow-100 rounded-xl p-5">
              <ul className="space-y-2 text-gray-800 list-disc list-inside">
                <li>PAN card of the applicant or business entity.</li>
                <li>
                  Identity and address proof of applicant (Aadhaar, Voter ID,
                  Passport, or Driving License).
                </li>
                <li>
                  Business address proof including rent/lease agreement, property
                  tax receipt, or recent utility bills.
                </li>
                <li>
                  Nature of business details including description of trade
                  activities and premises layout plan.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US SECTION */}
      <section className="border-t border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto px-4 py-10 grid md:grid-cols-2 gap-12">
          {/* Why choose us */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Why Choose Hawkins &amp; Brooks?
            </h2>
            <p className="text-gray-700 mb-4">
              The firm delivers municipal compliance expertise with a focus on
              accurate documentation and reliable service execution.
            </p>
            <div className="space-y-3">
              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Municipal Compliance Expertise
                </h3>
                <p className="text-gray-700 text-sm">
                  In-depth knowledge of local municipal regulations, trade
                  categories, and jurisdiction-specific documentation standards.
                </p>
              </div>

              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Accurate Documentation Support
                </h3>
                <p className="text-gray-700 text-sm">
                  Professional document structuring and verification to meet
                  municipal scrutiny standards and secure approvals efficiently.
                </p>
              </div>

              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Reliable Professional Service
                </h3>
                <p className="text-gray-700 text-sm">
                  End-to-end handling from application preparation through
                  municipal coordination and license issuance confirmation.
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="flex flex-col justify-center">
            <div className="bg-gray-50 border border-yellow-100 rounded-2xl p-6 shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Obtain Your Trade License Today
              </h3>
              <p className="text-gray-700 mb-5">
                Connect with Hawkins &amp; Brooks to secure your Trade License
                registration and ensure your business operates legally under
                local municipal regulations.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center px-6 py-2.5 rounded-full bg-green-500 text-black font-semibold shadow hover:bg-green-400 transition-colors text-sm"
                >
                  Get Consultation
                </a>
                <a
                  href="tel:+919217468906"
                  className="inline-flex items-center justify-center px-5 py-2.5 rounded-full border border-yellow-500 text-yellow-600 font-medium hover:bg-yellow-50 transition-colors text-sm"
                >
                  Call Hawkins &amp; Brooks
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
